"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, FileCheck, Headphones, Loader as Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const highlights = [{
  title: "Application Assessment",
  description: "In-depth review of your analytical requirements and compliance thresholds."
}, {
  title: "Fast Response",
  description: "Direct response from our technical specialists within 24 business hours."
}, {
  title: "Regional Support",
  description: "Dedicated service engineers and laboratory specialists across Ireland and the UK."
}];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/50 rounded-lg border border-border">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Request Received</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          {successMessage || "Thank you. An analytical technology specialist will review your inquiry and contact you shortly."}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="Dr. Sarah Murphy" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="sarah.murphy@pharma.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="organization" rules={{
        required: "Company or institution is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Organization *</FormLabel>
              <FormControl>
                <Input placeholder="Company or Laboratory Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="applicationArea" rules={{
        required: "Please select an area of focus"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Application Area *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select primary interest" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="powder-testing">Powder Testing & Rheology</SelectItem>
                  <SelectItem value="pat-monitoring">Process Analytical Technology (PAT)</SelectItem>
                  <SelectItem value="tablet-testing">Tablet Physical Testing</SelectItem>
                  <SelectItem value="blend-uniformity">Blend Uniformity & Drying</SelectItem>
                  <SelectItem value="raw-material-id">Raw Material Identification</SelectItem>
                  <SelectItem value="hplc-recovery">HPLC Solvent Recovery</SelectItem>
                  <SelectItem value="qualification-service">Qualification & Lifecycle Support</SelectItem>
                  <SelectItem value="other">Other Technical Inquiry</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="projectDetails" rules={{
      required: "Please briefly describe your application or inquiry",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Application Details & Requirements *</FormLabel>
            <FormControl>
              <Textarea placeholder="Outline your analytical challenge, instrument specifications, sample matrix, or timeline requirements..." rows={4} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full font-medium" size="lg">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-4 mr-2" />
            Submitting Request...
          </> : <>
            Request Technical Consultation
            <ArrowRight className="size-4 ml-1" />
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground pt-1">
        We respect your privacy. Technical information shared is treated with strict confidentiality.
      </p>
    </Form>;
}
export default function Consultation() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="consultationinquirycta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Context & Guarantees */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="gap-1.5 px-3 py-1 text-xs">
                <Sparkles className="size-3.5 text-primary" />
                Technical Advisory
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                Discuss Your Analytical & Qualification Requirements
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Connect directly with our application engineers for custom instrument recommendations, feasibility trials, method transfer support, and compliance guidance across Ireland and the UK.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {highlights.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-secondary/60 border border-border">
                  <div className="mt-0.5 rounded-md bg-primary/10 p-1.5 text-primary shrink-0">
                    {index === 0 && <FileCheck className="size-4" />}
                    {index === 1 && <Clock className="size-4" />}
                    {index === 2 && <ShieldCheck className="size-4" />}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/SolutionsPage">
                  Explore Solutions
                </Link>
              </Button>
              <Button asChild variant="ghost" className="w-full sm:w-auto">
                <Link to="/ContactPage" className="inline-flex items-center gap-1.5">
                  <Headphones className="size-4" />
                  General Contact
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Interactive Consultation Request Form */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="border-border shadow-md bg-card">
              <CardHeader className="border-b border-border pb-5">
                <CardTitle className="text-xl font-semibold text-foreground">
                  Application & Consultation Inquiry
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Provide brief details regarding your testing objectives or instrumentation needs.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="consultation-inquiry-cta" sectionName="consultation-inquiry" defaultValues={{
                fullName: "",
                email: "",
                organization: "",
                applicationArea: "",
                projectDetails: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                organization: {
                  required: true
                },
                applicationArea: {
                  required: true
                },
                projectDetails: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Request Technical Consultation" successMessage="Thank you. Your consultation request has been submitted to our technical team.">
                  <ConsultationFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}